import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Button } from '@mdvip/shared/components/ui/button';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { MembershipCardData } from '@/hooks/membership/useMembershipCard';
import { MembershipCardArt } from './MembershipCardArt';

/** What the browser's print header and the saved-PDF filename read, matching legacy. */
const PRINT_DOCUMENT_TITLE = 'MDVIP Membership Card';

function printCard() {
  const previousTitle = document.title;
  document.title = PRINT_DOCUMENT_TITLE;
  try {
    window.print();
  } finally {
    document.title = previousTitle;
  }
}

interface MembershipCardModalProps {
  open: boolean;
  onClose: () => void;
  data: MembershipCardData;
}

/**
 * The card, full size, front and back, with a print action.
 *
 * Printing goes through a second copy of the artwork portalled straight into `document.body` and
 * marked `data-print-root`; `membership-card.css` hides every other body child under
 * `@media print`. Printing the modal's own DOM instead would mean fighting the dialog — it is
 * portalled outside `#root` and its panel is height-capped with `overflow: hidden`, so the card
 * would be clipped and the page chrome would come along with it.
 */
export function MembershipCardModal({ open, onClose, data }: MembershipCardModalProps) {
  const { t } = useTranslation('members');

  return (
    <>
      <ResponsiveModal open={open} onOpenChange={(next) => !next && onClose()} className="sm:max-w-3xl">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{t('profile.membership.modal.title')}</ResponsiveModalTitle>
          <ResponsiveModalDescription>{t('profile.membership.modal.description')}</ResponsiveModalDescription>
        </ResponsiveModalHeader>
        <ResponsiveModalBody>
          <MembershipCardArt side="both" data={data} />
        </ResponsiveModalBody>
        <ResponsiveModalFooter>
          <Button variant="outline" onClick={printCard}>
            <Icon name="print" size="sm" />
            {t('profile.membership.actions.printCard')}
          </Button>
          <Button onClick={onClose}>{t('profile.membership.actions.done')}</Button>
        </ResponsiveModalFooter>
      </ResponsiveModal>
      {open &&
        createPortal(
          <div data-print-root aria-hidden="true">
            <MembershipCardArt side="both" data={data} />
          </div>,
          document.body,
        )}
    </>
  );
}
